import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, organization } from '@cwos/api-client';
import { Button, Input, Pill, Select, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { listPhase } from '../listState.js';
import { personDirectory } from '../personDirectory.js';
import { SettingsCard, SettingsHeader } from './common.js';
import {
  endRecordedLate,
  isDefaultScope,
  reproducibilityNote,
  revisionActLabel,
  revisionScopeLabel,
  scopeLabel,
  summarizeResolution,
  windowLabel,
  windowStateOf,
  type OperatingJurisdiction,
} from './jurisdictionsLogic.js';

/**
 * ===========================================================================
 * SETTINGS → OPERATING JURISDICTIONS
 * ===========================================================================
 *
 * WHERE THIS ORGANIZATION OPERATES. Not what external law says — that is
 * platform-published knowledge with its own authority model, and the two
 * domains meet only where Phibian asks which external rules apply HERE.
 *
 * ---------------------------------------------------------------------------
 * FOUR STATES ON THE WAY IN, AND FOUR MORE ON THE WAY OUT
 * ---------------------------------------------------------------------------
 * They are different questions and they use different instruments:
 *
 *   `authorityGate` + `GateNotice`   MAY THIS READER SEE THE SCREEN — checking
 *                                    / couldn't check / no / yes. Never a bare
 *                                    `has(...)`, because `has()` is false while
 *                                    `/auth/me` is still in flight and a
 *                                    refusal sentence built from that accuses
 *                                    somebody of lacking access they hold.
 *   `listPhase` + `ListState`        WHAT DID THE LIST ANSWER — loading /
 *                                    refused / failed / empty / stale / rows.
 *   `summarizeResolution`            WHAT DID THE RESOLVER ANSWER — resolved /
 *                                    wide / nothing recorded / refused for that
 *                                    context. "Nothing recorded" is NOT "no
 *                                    requirements apply" and the copy says so.
 *
 * ---------------------------------------------------------------------------
 * TWO THINGS THIS SCREEN REFUSES TO SAY
 * ---------------------------------------------------------------------------
 * 1. A CLOSED WINDOW IS NOT CURRENT. `windowStateOf` decides it from the dates,
 *    every row carries `data-window`, and an ended row is labelled "Ended". A
 *    history screen that showed a lapsed licence as in force would be worse
 *    than no history screen.
 * 2. AN ORGANIZATION DEFAULT IS NOT A FACT ABOUT A PROGRAM. Org-scoped rows are
 *    labelled "Organization default" and carry `data-default="true"`, because
 *    the resolver reads them LAST and a reader who took one for the answer
 *    everywhere would have the precedence backwards.
 *
 * There is no edit control anywhere on this screen, and that is deliberate
 * rather than unfinished: a recorded jurisdiction is immutable
 * (1861000000000). A mistake is ended and re-recorded, so the history keeps
 * saying what it said.
 */

/** The key the routes charge. Org-wide only; a program-scoped grant opens nothing. */
const CONFIGURE = 'jurisdiction:configure';

const SCOPE_OPTIONS = [
  { value: 'org', label: 'The whole organization (a default)' },
  { value: 'program', label: 'One program' },
  { value: 'location', label: 'One site' },
  { value: 'service', label: 'One service type' },
] as const;

type ScopeKind = (typeof SCOPE_OPTIONS)[number]['value'];

/** `YYYY-MM-DD` in the reader's own day, computed once per render pass. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

export function JurisdictionsSettings() {
  const permissions = usePermissions();
  const gate = authorityGate(permissions, CONFIGURE);
  const queryClient = useQueryClient();
  const [filterId, setFilterId] = useState('');

  const list = useQuery({
    queryKey: ['jurisdictions', 'list'],
    queryFn: () => organization.jurisdictions.list(),
    // NOT gated on the permission. `UNKNOWN-IS-NOT-DENIED-FRONTEND.md` names
    // `WorkerProfile.tsx:96` for exactly this: `enabled: has(key)` makes the
    // screen refuse AND fetch nothing on a permission the reader may hold. The
    // gate decides what is RENDERED; the server decides what is answered.
    enabled: gateOpen(gate),
  });
  const canReadDirectory = mayListWorkers(permissions);
  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-jurisdiction-revisions'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );
  const revisions = useQuery({
    queryKey: ['jurisdictions', 'revisions', filterId === '' ? null : filterId],
    queryFn: () => organization.jurisdictions.revisions(filterId === '' ? undefined : filterId),
    enabled: gateOpen(gate),
  });

  const [scopeType, setScopeType] = useState<ScopeKind>('org');
  const [jurisdiction, setJurisdiction] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [endingId, setEndingId] = useState<string | null>(null);
  const [endDate, setEndDate] = useState('');

  const record = useMutation({
    mutationFn: () =>
      organization.jurisdictions.record({
        jurisdiction: jurisdiction.trim().toUpperCase(),
        scopeType,
        ...(scopeType === 'program' ? { scopeProgramId: scopeId } : {}),
        ...(scopeType === 'location' ? { scopeLocationId: scopeId } : {}),
        ...(scopeType === 'service' ? { scopeServiceTypeId: scopeId } : {}),
        effectiveFrom,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setJurisdiction('');
      setScopeId('');
      setNote('');
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ['jurisdictions'] });
    },
    onError: (err: unknown) => {
      setFormError(
        (err as { message?: string } | null)?.message ??
          'That could not be recorded. Check the jurisdiction code and the dates.',
      );
    },
  });

  const end = useMutation({
    mutationFn: (id: string) => organization.jurisdictions.end(id, endDate),
    onSuccess: () => {
      setEndingId(null);
      setEndDate('');
      void queryClient.invalidateQueries({ queryKey: ['jurisdictions'] });
    },
  });

  const [askProgramId, setAskProgramId] = useState('');
  const [askAt, setAskAt] = useState('');
  const resolve = useQuery({
    queryKey: ['jurisdictions', 'resolve', askProgramId, askAt],
    queryFn: () =>
      organization.jurisdictions.resolve({
        ...(askProgramId ? { programId: askProgramId } : {}),
        ...(askAt ? { at: askAt } : {}),
      }),
    enabled: gateOpen(gate) && (askProgramId !== '' || askAt !== ''),
  });

  if (!gateOpen(gate)) {
    return (
      <>
        <SettingsHeader
          title="Operating jurisdictions"
          blurb="Where this organization operates, and since when."
        />
        <GateNotice
          gate={gate}
          what="see where this organization operates"
          heading="You can’t see operating jurisdictions"
          detail={
            'This screen needs the permission that configures where the organization operates ' +
            '(jurisdiction:configure). It is separate from managing the organization’s other ' +
            'settings, because it decides which external requirements reach your work.'
          }
          onRetry={() => void permissions.refetch?.()}
        />
      </>
    );
  }

  const rows = list.data ?? [];
  const today = todayIso();
  const phase = listPhase(list, rows.length);
  const answer = resolve.data ? summarizeResolution(resolve.data.jurisdiction) : null;
  const historyNote = resolve.data ? reproducibilityNote(resolve.data.asKnownAt) : null;

  return (
    <>
      <SettingsHeader
        title="Operating jurisdictions"
        blurb={
          'Where this organization operates — organization-wide, or narrowed to a program, site ' +
          'or service. This is what decides which external requirements reach your work. It does ' +
          'not change what those requirements say.'
        }
      />

      <SettingsCard>
        <h2 className="rn-card-title">Recorded jurisdictions</h2>
        <ListState
          phase={phase}
          surface="table"
          failedMessage="We couldn’t load your operating jurisdictions just now."
          onRetry={() => void list.refetch()}
          empty={
            <SuccessEmpty
              heading="Nothing recorded yet"
              message={
                'This is not the same as “no external requirements apply” — it means Phibian ' +
                'cannot say which ones do. Record where this organization operates.'
              }
            />
          }
        >
          <table data-testid="jurisdiction-table">
            <thead>
              <tr>
                <th scope="col">Jurisdiction</th>
                <th scope="col">Applies to</th>
                <th scope="col">In force</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="rn-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: OperatingJurisdiction) => {
                const state = windowStateOf(row, today);
                const badge = windowLabel(state);
                return (
                  <tr
                    key={row.id}
                    data-row="jurisdiction"
                    data-window={state}
                    data-default={String(isDefaultScope(row.scope))}
                  >
                    <td>{row.jurisdiction}</td>
                    <td>{scopeLabel(row.scope)}</td>
                    <td>
                      {row.effectiveFrom} → {row.effectiveTo ?? 'open'}
                    </td>
                    <td>
                      <Pill variant={badge.variant}>{badge.label}</Pill>
                    </td>
                    <td>
                      <Button variant="quiet" onClick={() => setFilterId(row.id)}>
                        History
                      </Button>
                      {/* NO EDIT CONTROL. A recorded jurisdiction is immutable;
                          the only change is ending it, and an ended one has no
                          verb left at all. */}
                      {state === 'ended' ? null : endingId === row.id ? (
                        <>
                          <label htmlFor={`end-${row.id}`}>Ended on</label>
                          <Input
                            id={`end-${row.id}`}
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.currentTarget.value)}
                          />
                          <Button
                            variant="primary"
                            disabled={endDate === '' || end.isPending}
                            onClick={() => end.mutate(row.id)}
                          >
                            Confirm end date
                          </Button>
                        </>
                      ) : (
                        <Button variant="quiet" onClick={() => setEndingId(row.id)}>
                          End this
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ListState>
      </SettingsCard>

      <div data-testid="jurisdiction-revisions">
        <SettingsCard>
          <h2 className="rn-card-title">What the record has said</h2>
          <p className="rn-muted">
            Every state a recorded jurisdiction has held, in the order the record came to say it. An
            end recorded after the date it took effect is shown as exactly that.
          </p>
          <label htmlFor="jurisdiction-history-filter">Show history for</label>
          <Select
            id="jurisdiction-history-filter"
            aria-label="Show history for"
            value={filterId}
            onChange={(e) => setFilterId(e.currentTarget.value)}
          >
            <option value="">All jurisdictions</option>
            {rows.map((row: OperatingJurisdiction) => (
              <option key={row.id} value={row.id}>
                {row.jurisdiction} · {scopeLabel(row.scope)}
              </option>
            ))}
          </Select>
          <ListState
            phase={listPhase(revisions, revisions.data?.length)}
            surface="table"
            failedMessage="We couldn’t load jurisdiction history just now."
            onRetry={() => void revisions.refetch()}
            empty={
              <SuccessEmpty
                heading="No history recorded yet."
                message="The record has no revisions to show."
              />
            }
          >
            <table>
              <thead>
                <tr>
                  <th scope="col">Recorded</th>
                  <th scope="col">Act</th>
                  <th scope="col">By</th>
                  <th scope="col">Jurisdiction</th>
                  <th scope="col">Applies to</th>
                  <th scope="col">In force</th>
                </tr>
              </thead>
              <tbody>
                {(revisions.data ?? []).map((revision) => {
                  const late = endRecordedLate(revision);
                  return (
                    <tr
                      key={revision.id}
                      data-row="revision"
                      data-act={revision.act}
                      data-late={String(late)}
                    >
                      <td>{new Date(revision.recordedAt).toLocaleString()}</td>
                      <td>
                        <Pill variant={revision.act === 'recorded' ? 'good' : 'plain'}>
                          {revisionActLabel(revision.act)}
                        </Pill>
                      </td>
                      <td>
                        {revision.actorKind === 'system'
                          ? 'System'
                          : people.name(revision.recordedByUserId)}
                      </td>
                      <td>{revision.jurisdiction}</td>
                      <td>{revisionScopeLabel(revision.scopeType)}</td>
                      <td>
                        {revision.effectiveFrom} → {revision.effectiveTo ?? 'open'}
                        {late ? (
                          <>
                            {' '}
                            <span className="rn-muted">Recorded after it took effect</span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ListState>
        </SettingsCard>
      </div>

      <SettingsCard>
        <h2 className="rn-card-title">Record where you operate</h2>
        <p className="rn-muted">
          A jurisdiction you record cannot be edited afterwards — ending it and recording a new one
          is how a correction is made, so the history keeps saying what it said.
        </p>
        <label htmlFor="jur-code">Jurisdiction code</label>
        <Input
          id="jur-code"
          value={jurisdiction}
          placeholder="US-MN"
          onChange={(e) => setJurisdiction(e.currentTarget.value)}
        />
        <label htmlFor="jur-scope">Applies to</label>
        <Select
          id="jur-scope"
          value={scopeType}
          onChange={(e) => setScopeType(e.currentTarget.value as ScopeKind)}
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        {scopeType === 'org' ? (
          <p className="rn-muted" data-testid="default-warning">
            An organization default applies only where nothing more specific is recorded. It never
            overrides a program, site or service jurisdiction.
          </p>
        ) : (
          <>
            <label htmlFor="jur-scope-id">Which one</label>
            <Input
              id="jur-scope-id"
              value={scopeId}
              onChange={(e) => setScopeId(e.currentTarget.value)}
            />
          </>
        )}
        <label htmlFor="jur-from">In force from</label>
        <Input
          id="jur-from"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
        />
        <label htmlFor="jur-note">Why (a licence number, a contract, a board decision)</label>
        <Input id="jur-note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        {formError ? (
          <p className="rn-muted" role="alert" data-testid="record-error">
            {formError}
          </p>
        ) : null}
        <Button
          variant="primary"
          disabled={jurisdiction.trim() === '' || effectiveFrom === '' || record.isPending}
          onClick={() => record.mutate()}
        >
          Record jurisdiction
        </Button>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Which jurisdictions govern a context</h2>
        <label htmlFor="ask-program">Program (optional)</label>
        <Input
          id="ask-program"
          value={askProgramId}
          onChange={(e) => setAskProgramId(e.currentTarget.value)}
        />
        <label htmlFor="ask-at">On this date (optional)</label>
        <Input
          id="ask-at"
          type="date"
          value={askAt}
          onChange={(e) => setAskAt(e.currentTarget.value)}
        />
        {answer ? (
          <div data-testid="resolution" data-resolution={answer.kind}>
            <p>{answer.headline}</p>
            <p className="rn-muted">{answer.detail}</p>
            {answer.fromDefault ? (
              <p className="rn-muted" data-testid="from-default">
                This is the organization default, not a jurisdiction recorded for this context.
              </p>
            ) : null}
            {historyNote ? (
              <p className="rn-muted" data-testid="not-reproducible">
                {historyNote}
              </p>
            ) : null}
          </div>
        ) : null}
      </SettingsCard>
    </>
  );
}
